/**
 * How the recipient is named back to the operator after the payout exists.
 *
 * Bre-B has no typed name — the directory owns it — so the confirmation screen, the result screen and the
 * review row have to agree on what to show, and they have to agree on the MASKED form. Keeping the mask in
 * one place is the point: the review row had its own copy, and a result screen that masked a key differently
 * from the screen the operator confirmed on reads as a different payment.
 */

/** Bre-B key, masked for display — enough to verify, not enough to copy out of the screen. */
export function maskBrebKey(type: string, key: string): string {
  const v = key.trim();
  if (!v) return "—";
  if (type === "EMAIL") {
    const parts = v.split("@");
    if (parts.length < 2) return v;
    return parts[0].slice(0, 2) + "***@" + parts[1];
  }
  if (type === "PHONE") {
    const digits = v.replace(/\D/g, "");
    return digits.length >= 10 ? "(" + digits.slice(0, 3) + ") ***-" + digits.slice(-4) : v;
  }
  if (type === "NRIC") return v.length > 4 ? "****" + v.slice(-4) : v;
  // ALPHANUM / MERCHANT — the alias is meant to be shared/verified, so show it in full.
  return v;
}

export interface RecipientDisplayInput {
  /** What the operator typed, on the rails that still ask. Empty on Bre-B. */
  typedName?: string | null;
  /** The directory's masked holder, when it answered. */
  resolvedHolder?: string | null;
  brebKeyType?: string | null;
  brebKeyValue?: string | null;
}

/**
 * The best name available for the recipient, in order of how much it was verified.
 *
 * A snapshot, taken when the payout is created and carried into the result screen — never re-derived there.
 * Once the money has moved, asking the directory again can only produce a different answer than the one the
 * operator approved, and the screen's job is to report what was sent.
 */
export function resolveRecipientDisplay(input: RecipientDisplayInput): string {
  const holder = input.resolvedHolder?.trim();
  if (holder) return holder;
  const typed = input.typedName?.trim();
  if (typed) return typed;
  // Bre-B with a directory that never answered: the key is all we have, and it is still a true statement
  // about where the money went.
  if (input.brebKeyValue?.trim()) {
    return maskBrebKey(String(input.brebKeyType ?? ""), input.brebKeyValue);
  }
  return "—";
}
